import {
  Camera, Upload, Loader2, ImageIcon, Check, Sparkles,
} from "lucide-react";
import { fileUrl } from "../../lib/api";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../../lib/bookingStatus";

/**
 * Feb 2026 · Zeile einer Kunden-Anfrage in der Live-Checkliste.
 * Extrahiert aus `TeamCustomerDetail.jsx` (Refactor Feb 2026) —
 * Verhalten unverändert, Farben aus zentralem Palette-Modul.
 *
 * Props (unverändert vom Original):
 *   it, busy, uploadingFor, onToggle, onCamera, onGallery,
 *   cameraRef, galleryRef, onFileChosen
 */
export function TaskRow({
  it, busy, uploadingFor,
  onToggle, onCamera, onGallery,
  cameraRef, galleryRef, onFileChosen,
}) {
  return (
    <li
      className="bg-white border border-navy/10 p-3 md:p-4 flex items-start gap-3"
      style={it.done
        ? { background: withAlpha(STATUS_COLORS.green, 0.06), borderColor: withAlpha(STATUS_COLORS.green, 0.25) }
        : {}}
      data-testid={`customer-task-${it.id}${it.done ? "-done" : ""}`}
    >
      <button
        type="button"
        onClick={() => onToggle(it)}
        disabled={busy}
        data-testid={`customer-task-toggle-${it.id}`}
        className="shrink-0 mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors disabled:opacity-50"
        style={it.done
          ? { background: STATUS_COLORS.green, borderColor: STATUS_COLORS.green }
          : { borderColor: withAlpha(BRAND_COLORS.navy, 0.35), background: BRAND_COLORS.white }}
        aria-pressed={it.done}
        title={it.done ? "Als offen markieren" : "Als erledigt markieren"}
      >
        {busy
          ? <Loader2 className="w-3 h-3 animate-spin" style={{ color: it.done ? BRAND_COLORS.ivory : BRAND_COLORS.navy }} />
          : it.done && <Check className="w-3.5 h-3.5" style={{ color: BRAND_COLORS.ivory }} strokeWidth={3} />}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="label-caps" style={{ color: BRAND_COLORS.gold }}>{it.source_label}</span>
          {it.created_by_team && (
            <span
              className="inline-flex items-center gap-1 text-[9.5px] tracking-[0.12em] uppercase px-1.5 py-0.5 rounded-full border"
              style={{
                color: BRAND_COLORS.goldDark,
                borderColor: withAlpha(BRAND_COLORS.gold, 0.5),
                background: withAlpha(BRAND_COLORS.gold, 0.14),
              }}
              data-testid={`customer-task-created-by-team-${it.id}`}
              title="Vom Team im Auftrag des Kunden angelegt"
            >
              <Sparkles className="w-2.5 h-2.5" strokeWidth={2} />
              von Weissblom
            </span>
          )}
          {it.priority && it.priority !== "normal" && (
            <span
              className="label-caps"
              style={{ color: it.priority === "urgent" ? STATUS_COLORS.red : BRAND_COLORS.gold }}
            >
              · {it.priority === "urgent" ? "Dringend" : "Hoch"}
            </span>
          )}
        </div>
        <div className={`text-[14px] leading-snug mt-0.5 ${it.done ? "text-navy-dark/60 line-through decoration-navy-dark/25" : "text-navy"}`}>
          {it.label}
        </div>
        {it.description && (
          <div className={`text-[12px] mt-1 leading-relaxed ${it.done ? "text-navy-dark/45" : "text-navy-dark/65"}`}>
            {it.description}
          </div>
        )}
        {it.photo_file_id && (
          <a
            href={fileUrl(it.photo_file_id)}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 mt-2 text-[11px] tracking-wider uppercase text-navy-dark/70 hover:text-navy"
            data-testid={`customer-task-photo-${it.id}`}
          >
            <ImageIcon className="w-3.5 h-3.5" strokeWidth={1.6} />
            Foto anzeigen
          </a>
        )}
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => onFileChosen(e, it)}
          className="hidden"
          data-testid={`customer-task-camera-input-${it.id}`}
        />
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          onChange={(e) => onFileChosen(e, it)}
          className="hidden"
          data-testid={`customer-task-gallery-input-${it.id}`}
        />
        <button
          type="button"
          onClick={onCamera}
          disabled={busy}
          data-testid={`customer-task-camera-btn-${it.id}`}
          title={it.photo_file_id ? "Foto neu aufnehmen" : "Foto aufnehmen (Kamera)"}
          className="w-8 h-8 flex items-center justify-center border border-navy/15 hover:border-gold transition-colors disabled:opacity-50"
          style={it.photo_file_id
            ? { background: withAlpha(BRAND_COLORS.gold, 0.15), borderColor: withAlpha(BRAND_COLORS.gold, 0.5) }
            : {}}
        >
          {uploadingFor === it.id
            ? <Loader2 className="w-3.5 h-3.5 animate-spin text-navy" />
            : <Camera
                className="w-3.5 h-3.5"
                strokeWidth={1.6}
                style={{ color: it.photo_file_id ? BRAND_COLORS.gold : BRAND_COLORS.navy }}
              />}
        </button>
        <button
          type="button"
          onClick={onGallery}
          disabled={busy}
          data-testid={`customer-task-gallery-btn-${it.id}`}
          title="Aus Galerie / Datei wählen"
          className="w-8 h-8 flex items-center justify-center border border-navy/15 hover:border-gold transition-colors disabled:opacity-50"
        >
          <Upload className="w-3.5 h-3.5" strokeWidth={1.6} />
        </button>
      </div>
    </li>
  );
}
